import React from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, useReactFlow, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Save, CheckCircle2, AlertTriangle, PlayCircle, Rocket, Lock, Plus,
  ChevronDown, ChevronUp, Loader2, XCircle,
} from "lucide-react";
import { nodeTypes, NODE_ACCENT, NODE_ICON } from "./nodeTypes";
import Inspector from "./Inspector";
import * as api from "../../api/workflows";

/**
 * The workflow editor.
 *
 * Published workflows open read-only. That is not a limitation to work around —
 * a published graph is what routed live records, so editing it in place would
 * rewrite history and silently reroute work already in flight. "Edit" opens a
 * draft at the next version instead, and publishing swaps them atomically.
 */

const EDGE_STYLES = {
  primary: { stroke: "var(--cares-purple)", strokeWidth: 2 },
  conditional: { stroke: "var(--cares-amber)", strokeWidth: 1.6 },
  fallback: { stroke: "var(--cares-text-note)", strokeWidth: 1.4, strokeDasharray: "5 4" },
  plain: { stroke: "var(--cares-text-dim)", strokeWidth: 1.5 },
};

function edgeKind(data = {}) {
  if (data.isPrimary) return "primary";
  if (data.isDefault) return "fallback";
  if (data.condition) return "conditional";
  return "plain";
}

/** Graph (as stored) -> React Flow props, applying the visual language above. */
function toFlowEdges(edges = []) {
  return edges.map((e) => {
    const kind = edgeKind(e.data);
    return {
      ...e,
      type: "smoothstep",
      label: e.data?.label || undefined,
      animated: kind === "primary",
      style: EDGE_STYLES[kind],
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_STYLES[kind].stroke },
      labelStyle: { fontSize: 10, fontWeight: 600, fill: "var(--cares-surface-board)" },
      labelBgStyle: { fill: "var(--cares-surface-card)", fillOpacity: 0.92 },
      labelBgPadding: [5, 3],
      labelBgBorderRadius: 4,
    };
  });
}

const btn = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px",
  borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: "1px solid var(--cares-line-strong)", background: "var(--cares-surface-card)", color: "var(--cares-text-2)",
};
const btnPrimary = { ...btn, background: "var(--cares-purple)", borderColor: "var(--cares-purple)", color: "var(--cares-text-max)" };

function Canvas({ workflow, palette, actors, onSaved, onPublished }) {
  const readOnly = workflow.status !== "DRAFT";
  const [nodes, setNodes, onNodesChange] = useNodesState(workflow.graph.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(toFlowEdges(workflow.graph.edges));
  const [selection, setSelection] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState("");
  const [validation, setValidation] = React.useState(workflow.validation || null);
  const [sim, setSim] = React.useState(null);
  const [panel, setPanel] = React.useState("validation");
  const [panelOpen, setPanelOpen] = React.useState(true);
  const [message, setMessage] = React.useState(null);
  const { screenToFlowPosition } = useReactFlow();

  // Strip the presentation-only fields back off before persisting, so the stored
  // graph stays the portable definition rather than a dump of React Flow state.
  const currentGraph = React.useCallback(() => ({
    nodes: nodes.map(({ id, type, position, data }) => ({ id, type, position, data })),
    edges: edges.map(({ id, source, target, data }) => ({ id, source, target, data })),
  }), [nodes, edges]);

  const markDirty = () => { setDirty(true); setMessage(null); };

  const onConnect = React.useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      id: `e-${params.source}-${params.target}-${eds.length}`,
      data: { label: null, condition: null, isDefault: false, isPrimary: false },
      type: "smoothstep",
      style: EDGE_STYLES.plain,
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_STYLES.plain.stroke },
    }, eds));
    markDirty();
  }, [setEdges]);

  const addNode = (type) => {
    const id = `${type}-${Date.now().toString(36)}`;
    const pos = screenToFlowPosition({
      x: window.innerWidth / 2 - 60 + (nodes.length % 5) * 24,
      y: 300 + (nodes.length % 5) * 20,
    });
    const defaults = {
      start: { label: "Start" },
      stage: { label: "New stage", stageKey: "", assignees: [], slaHours: null },
      decision: { label: "New decision" },
      automation: { label: "New automation", action: "" },
      end: { label: "End", outcome: "COMPLETED" },
    };
    setNodes((ns) => [...ns, { id, type, position: pos, data: defaults[type] }]);
    markDirty();
  };

  const changeNode = (id, data) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data } : n)));
    setSelection((s) => (s && s.item.id === id
      ? { ...s, item: { ...s.item, data } } : s));
    markDirty();
  };

  const changeEdge = (id, data) => {
    const kind = edgeKind(data);
    setEdges((es) => es.map((e) => (e.id === id ? {
      ...e, data, label: data.label || undefined,
      animated: kind === "primary", style: EDGE_STYLES[kind],
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_STYLES[kind].stroke },
    } : e)));
    setSelection((s) => (s && s.item.id === id
      ? { ...s, item: { ...s.item, data } } : s));
    markDirty();
  };

  const remove = (sel) => {
    if (sel.kind === "edge") setEdges((es) => es.filter((e) => e.id !== sel.item.id));
    else {
      setNodes((ns) => ns.filter((n) => n.id !== sel.item.id));
      // Leaving edges pointing at a deleted node would fail validation with a
      // message about a step that is no longer on screen to fix.
      setEdges((es) => es.filter((e) => e.source !== sel.item.id && e.target !== sel.item.id));
    }
    setSelection(null);
    markDirty();
  };

  const runValidate = async (graph) => {
    const result = await api.validateGraph(graph || currentGraph());
    setValidation(result);
    setPanel("validation"); setPanelOpen(true);
    return result;
  };

  const save = async () => {
    setBusy("save"); setMessage(null);
    try {
      const graph = currentGraph();
      const saved = await api.saveWorkflow(workflow.id, { graph, actor: "mmohen" });
      setValidation(saved.validation);
      setDirty(false);
      setMessage({ tone: "ok", text: "Draft saved." });
      onSaved?.(saved);
    } catch (e) {
      setMessage({ tone: "err", text: e.message || String(e) });
    } finally { setBusy(""); }
  };

  const publish = async () => {
    setBusy("publish"); setMessage(null);
    try {
      if (dirty) await api.saveWorkflow(workflow.id, { graph: currentGraph(), actor: "mmohen" });
      const res = await api.publishWorkflow(workflow.id, "mmohen");
      setDirty(false);
      setMessage({ tone: "ok", text: res.message });
      onPublished?.(res);
    } catch (e) {
      // A publish refused by validation carries the reasons; show them rather
      // than a bare "422".
      const detail = e.detail;
      if (detail?.errors) {
        setValidation({ valid: false, errors: detail.errors, warnings: detail.warnings || [] });
        setPanel("validation"); setPanelOpen(true);
        setMessage({ tone: "err", text: detail.message || "This workflow cannot be published yet." });
      } else {
        setMessage({ tone: "err", text: e.message || String(e) });
      }
    } finally { setBusy(""); }
  };

  const [simEntity, setSimEntity] = React.useState("");
  const runSimulate = async () => {
    setBusy("sim"); setMessage(null);
    try {
      const res = await api.simulate({
        graph: currentGraph(),
        entityId: simEntity.trim() || undefined,
      });
      setSim(res); setPanel("simulation"); setPanelOpen(true);
    } catch (e) {
      setMessage({ tone: "err", text: e.message || String(e) });
    } finally { setBusy(""); }
  };

  const errors = validation?.errors || [];
  const warnings = validation?.warnings || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 190px)",
                  minHeight: 560, border: "1px solid var(--cares-line-card)", borderRadius: 14,
                  overflow: "hidden", background: "var(--cares-surface-card)" }}>
      {/* toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    borderBottom: "1px solid var(--cares-line-card)", background: "var(--cares-surface-board)", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--cares-text-1)" }}>
              {workflow.name}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                           background: workflow.status === "PUBLISHED" ? "color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))"
                                     : workflow.status === "DRAFT" ? "color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))" : "var(--cares-surface-board)",
                           color: workflow.status === "PUBLISHED" ? "var(--cares-green)"
                                : workflow.status === "DRAFT" ? "var(--cares-amber)" : "var(--cares-text-3)" }}>
              {workflow.status} · v{workflow.version}
            </span>
            {dirty && <span style={{ fontSize: 10.5, color: "var(--cares-amber)" }}>unsaved changes</span>}
          </div>
          {message && (
            <div style={{ fontSize: 11.5, marginTop: 3,
                          color: message.tone === "ok" ? "var(--cares-green)" : "var(--cares-orange)" }}>
              {message.text}
            </div>
          )}
        </div>

        {readOnly ? (
          <span style={{ ...btn, cursor: "default", color: "var(--cares-text-label)" }}>
            <Lock size={12} /> Published workflows are read-only
          </span>
        ) : (
          <>
            <input value={simEntity} onChange={(e) => setSimEntity(e.target.value)}
                   placeholder="DEM-1005 (optional)"
                   style={{ padding: "6px 9px", fontSize: 11.5, borderRadius: 7,
                            border: "1px solid var(--cares-line-strong)", width: 140 }} />
            <button style={btn} onClick={runSimulate} disabled={!!busy}>
              {busy === "sim" ? <Loader2 size={13} className="animate-spin" />
                              : <PlayCircle size={13} />} Simulate
            </button>
            <button style={btn} onClick={() => runValidate()} disabled={!!busy}>
              <CheckCircle2 size={13} /> Check
            </button>
            <button style={btn} onClick={save} disabled={!!busy || !dirty}>
              {busy === "save" ? <Loader2 size={13} /> : <Save size={13} />} Save draft
            </button>
            <button style={btnPrimary} onClick={publish} disabled={!!busy}>
              {busy === "publish" ? <Loader2 size={13} /> : <Rocket size={13} />} Publish
            </button>
          </>
        )}
      </div>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* palette */}
        {!readOnly && (
          <div style={{ width: 132, borderRight: "1px solid var(--cares-line-card)", padding: 10,
                        background: "var(--cares-surface-board)", overflowY: "auto" }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--cares-text-note)",
                          textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
              Add a step
            </div>
            {(palette?.nodeTypes || []).map((t) => {
              const Icon = NODE_ICON[t.type];
              const exists = t.single && nodes.some((n) => n.type === t.type);
              return (
                <button key={t.type} onClick={() => addNode(t.type)} disabled={exists}
                        title={exists ? "A workflow can only have one of these" : t.description}
                        style={{ ...btn, width: "100%", marginBottom: 6, padding: "7px 9px",
                                 justifyContent: "flex-start", fontSize: 11.5,
                                 opacity: exists ? .45 : 1,
                                 cursor: exists ? "not-allowed" : "pointer" }}>
                  <Icon size={12} style={{ color: NODE_ACCENT[t.type] }} />
                  {t.label}
                  <Plus size={10} style={{ marginLeft: "auto", color: "var(--cares-text-dim)" }} />
                </button>
              );
            })}
            <div style={{ marginTop: 14, fontSize: 9.5, color: "var(--cares-text-note)", lineHeight: 1.5 }}>
              Drag between the dots on each step to connect them.
            </div>
          </div>
        )}

        {/* canvas */}
        <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable
            onNodeClick={(_, node) => setSelection({ kind: "node", item: node })}
            onEdgeClick={(_, edge) => {
              const find = (id) => nodes.find((n) => n.id === id)?.data?.label || id;
              setSelection({ kind: "edge", item: edge,
                             sourceLabel: find(edge.source), targetLabel: find(edge.target) });
            }}
            onPaneClick={() => setSelection(null)}
            fitView
            fitViewOptions={{ padding: 0.18 }}
            proOptions={{ hideAttribution: false }}
          >
            <Background gap={18} size={1} color="var(--cares-text-note)" />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable nodeStrokeWidth={2}
                     nodeColor={(n) => NODE_ACCENT[n.type] || "#cbd5e1"}
                     style={{ background: "var(--cares-surface-board)", border: "1px solid var(--cares-line-card)" }} />
          </ReactFlow>
        </div>

        {/* inspector */}
        <div style={{ width: 288, borderLeft: "1px solid var(--cares-line-card)", overflowY: "auto",
                      background: "var(--cares-surface-card)" }}>
          <Inspector selection={selection} palette={palette} actors={actors}
                     domain={workflow.domain} readOnly={readOnly}
                     onChangeNode={changeNode} onChangeEdge={changeEdge} onDelete={remove} />
        </div>
      </div>

      {/* results */}
      <div style={{ borderTop: "1px solid var(--cares-line-card)", background: "var(--cares-surface-board)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "7px 14px" }}>
          {["validation", "simulation"].map((p) => (
            <button key={p} onClick={() => { setPanel(p); setPanelOpen(true); }}
                    style={{ background: "none", border: "none", cursor: "pointer",
                             fontSize: 11.5, fontWeight: 700, padding: "2px 0",
                             color: panel === p ? "var(--cares-purple)" : "var(--cares-text-note)",
                             borderBottom: panel === p ? "2px solid var(--cares-purple)" : "2px solid transparent",
                             textTransform: "capitalize" }}>
              {p}
              {p === "validation" && errors.length > 0 && (
                <span style={{ marginLeft: 5, color: "var(--cares-orange)" }}>({errors.length})</span>
              )}
            </button>
          ))}
          <button onClick={() => setPanelOpen((o) => !o)}
                  style={{ marginLeft: "auto", background: "none", border: "none",
                           cursor: "pointer", color: "var(--cares-text-note)" }}>
            {panelOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>

        {panelOpen && (
          <div style={{ maxHeight: 172, overflowY: "auto", padding: "0 14px 12px" }}>
            {panel === "validation" && (
              !validation ? (
                <p style={{ fontSize: 11.5, color: "var(--cares-text-note)" }}>
                  Run <strong>Check</strong> to see whether this workflow can be published.
                </p>
              ) : errors.length === 0 && warnings.length === 0 ? (
                <p style={{ fontSize: 12, color: "var(--cares-green)", display: "flex",
                            alignItems: "center", gap: 6 }}>
                  <CheckCircle2 size={14} /> No problems found — ready to publish.
                </p>
              ) : (
                <>
                  {errors.map((e, i) => (
                    <div key={`e${i}`} style={{ display: "flex", gap: 7, fontSize: 11.5,
                                                color: "var(--cares-orange)", padding: "3px 0" }}>
                      <XCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{e.message}</span>
                    </div>
                  ))}
                  {warnings.map((w, i) => (
                    <div key={`w${i}`} style={{ display: "flex", gap: 7, fontSize: 11.5,
                                                color: "var(--cares-amber)", padding: "3px 0" }}>
                      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{w.message}</span>
                    </div>
                  ))}
                  {errors.length === 0 && (
                    <p style={{ fontSize: 10.5, color: "var(--cares-text-note)", marginTop: 6 }}>
                      Warnings do not block publishing.
                    </p>
                  )}
                </>
              )
            )}

            {panel === "simulation" && (
              !sim ? (
                <p style={{ fontSize: 11.5, color: "var(--cares-text-note)" }}>
                  Run <strong>Simulate</strong> to walk a record through this workflow.
                  Enter a demand ID to use its real data.
                </p>
              ) : (
                <>
                  {sim.usedEntity && (
                    <p style={{ fontSize: 10.5, color: "var(--cares-text-label)", margin: "0 0 6px" }}>
                      Using live data from <strong>{sim.entityId}</strong>.
                    </p>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5 }}>
                    {sim.steps.map((s, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span style={{ color: "var(--cares-text-dim)", fontSize: 11 }}>→</span>}
                        <span title={(s.assignees || []).map((a) => a.name).join(", ")}
                              style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 7px",
                                       borderRadius: 6,
                                       background: s.type === "error" ? "color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))"
                                                 : s.type === "note" ? "color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))"
                                                 : `${NODE_ACCENT[s.type] || "var(--cares-surface-raised)"}18`,
                                       color: s.type === "error" ? "var(--cares-orange)"
                                            : s.type === "note" ? "var(--cares-amber)"
                                            : NODE_ACCENT[s.type] || "var(--cares-text-3)" }}>
                          {s.label}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  {sim.steps.some((s) => (s.decisions || []).length) && (
                    <div style={{ marginTop: 9 }}>
                      {sim.steps.flatMap((s) => (s.decisions || []).map((d, j) => (
                        <div key={`${s.nodeId}-${j}`}
                             style={{ fontSize: 10.5, color: "var(--cares-text-label)",
                                      fontFamily: "ui-monospace, Menlo, monospace" }}>
                          → {s.label}: {d.condition?.expression || "(default)"}
                          {d.condition?.fields && Object.keys(d.condition.fields).length > 0 && (
                            <span style={{ color: "var(--cares-text-note)" }}>
                              {"  "}[{Object.entries(d.condition.fields)
                                .map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(", ")}]
                            </span>
                          )}
                        </div>
                      )))}
                    </div>
                  )}
                </>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkflowEditor(props) {
  // useReactFlow (for screenToFlowPosition) requires the provider above it.
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
